/**
 * ReconciliationDashboard — historical IO ⇄ invoice review, scoped to campaigns
 * whose flight ENDED in the selected window (default: the last 120 days). Note a
 * mid-flight campaign does not appear until its flight ends — a deliberate choice.
 *
 * Standalone dashboard (gated by invoicing.view). Each campaign is reconciled per
 * BILLING MONTH (invoices are issued monthly, in arrears): un-billed IOs, un-issued
 * invoices, planned-vs-billed, billed-vs-issued-invoice-PDF, and untracked invoices.
 * Impressions and the tracker Paid status are shown for context only (no checks).
 *
 * The compute is heavy (Drive scan + IO-PDF extraction, optionally a GAM refresh)
 * so it runs detached on the server: "Run reconciliation" POSTs and we poll the
 * persisted result. The most recent completed report stays on screen while a new
 * run is in flight.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardFrame, KpiCard, Toast, ToastKind, LoadingDots, EmptyState, tableStyles } from '../ui';
import { getReconciliationStatus, getReconciliationReport, runReconciliation } from '../../services/reconciliationApi';
import MonthLedgerRow, { fmtUsd } from './MonthLedgerRow';
import {
  ReconciliationRunState,
  ReconciliationStatus,
  ReconciliationReport,
  ReconciliationFinding,
  ReconciliationSeverity,
} from './types';

const POLL_MS = 10 * 1000;
// How long after kicking a run we keep showing "running" before giving up waiting
// for the server to register it (covers the detached-202 write-then-read race).
const START_GRACE_MS = 30 * 1000;
const TOAST_MS = 6 * 1000;

// Summary-table columns. colCount drives the expanded month-ledger's colSpan, so the
// two never drift (mirrors the Invoicing dashboard's COL_COUNT guard).
const COLUMNS = ['', 'Severity', 'Advertiser', 'Order #', 'Flight Window', 'Completeness', 'Impressions', 'Billed', 'Invoiced', '$ At Risk', 'Checks'];
const COL_COUNT = COLUMNS.length;

const SEV_RANK: Record<ReconciliationSeverity, number> = { ok: 1, warning: 2, critical: 3 };
type SortKey = 'risk' | 'advertiser' | 'flightEnd' | 'severity';

/** Clamp a possibly-NaN number input to [lo, hi], falling back to a default. */
const clampInt = (v: number, lo: number, hi: number, dflt: number): number =>
  Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : dflt;

const fmtWhen = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const ReconciliationDashboard: React.FC = () => {
  const [status, setStatus] = useState<ReconciliationStatus | null>(null);
  const [state, setState] = useState<ReconciliationRunState | null>(null);
  const [lastReport, setLastReport] = useState<ReconciliationReport | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);
  const [minDays, setMinDays] = useState(1);
  const [maxDays, setMaxDays] = useState(120);
  // Default ON: the audit needs true billing (tracker) + true delivery (GAM). Uncheck
  // for a fast cached re-run once a refreshed run has written the data back.
  const [refreshGam, setRefreshGam] = useState(true);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<number | null>(null);
  // Wall-clock ms when WE kicked a run, or 0. Lets ingest tell "the server hasn't
  // registered our detached run yet" (keep showing running) apart from "the prior
  // run is genuinely complete" (stop) — the core of the post-202 race fix.
  const startedAtRef = useRef<number>(0);

  // View state (client-side only — no API change). Hybrid default: show flagged
  // campaigns; toggle off to also surface the clean (✓) ones for confidence.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(true);
  const [hidePending, setHidePending] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('risk');

  const ingest = useCallback((rep: { state: ReconciliationRunState | null; inFlight: boolean }) => {
    setState(rep.state);
    if (rep.state?.report) setLastReport(rep.state.report);

    const serverRunning = rep.inFlight || rep.state?.status === 'running';
    const pending = startedAtRef.current;
    if (pending <= 0) {
      setInFlight(serverRunning);
      return;
    }

    if (serverRunning) {
      setInFlight(true);
      return;
    }
    const terminal = rep.state?.status === 'complete' || rep.state?.status === 'failed';
    const serverStartMs = rep.state?.startedAt ? Date.parse(rep.state.startedAt) : 0;
    const serverHasOurRun = serverStartMs >= pending - 1000; // ~1s clock skew
    const graceElapsed = Date.now() - pending > START_GRACE_MS;
    if (terminal && (serverHasOurRun || graceElapsed)) {
      startedAtRef.current = 0;
      setInFlight(false);
      if (rep.state?.status === 'failed') {
        setToast({ kind: 'error', title: 'Reconciliation failed', message: rep.state?.error });
      } else {
        setToast({ kind: 'success', title: 'Reconciliation complete', message: `${rep.state?.report?.totals.findings ?? 0} finding(s) flagged.` });
      }
    } else if (graceElapsed) {
      startedAtRef.current = 0; // give up waiting for an unregistered run
      setInFlight(false);
    } else {
      setInFlight(true); // server hasn't picked up our run yet — keep waiting
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [st, rep] = await Promise.all([getReconciliationStatus(), getReconciliationReport()]);
      setStatus(st);
      ingest(rep);
    } catch (e: any) {
      setError(e?.message || 'Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [ingest]);

  useEffect(() => {
    load();
  }, [load]);

  // Single source of truth for polling: poll iff a run is in flight.
  useEffect(() => {
    if (!inFlight) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = window.setInterval(async () => {
      try {
        ingest(await getReconciliationReport());
      } catch {
        /* transient — keep polling */
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [inFlight, ingest]);

  // Auto-dismiss toasts.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  const onRun = async () => {
    if (starting || inFlight) return; // synchronous double-submit guard
    setStarting(true);
    setError(null);
    // Sanitize: enforce min < max and drop NaN/out-of-range before sending.
    const safeMin = clampInt(minDays, 0, 399, 1);
    const safeMax = Math.max(safeMin + 1, clampInt(maxDays, 1, 400, 120));
    try {
      startedAtRef.current = Date.now(); // set BEFORE await so ingest won't clobber inFlight
      const resp = await runReconciliation({ minDays: safeMin, maxDays: safeMax, export: true, refreshGam });
      setInFlight(true);
      if (resp?.status === 'already-running') {
        // Another run is already in flight — OUR parameters were ignored. Track that
        // run instead (clear startedAtRef so ingest follows the server's state).
        startedAtRef.current = 0;
        setToast({
          kind: 'info',
          title: 'A reconciliation is already running',
          message: 'Your settings were not applied — showing the in-progress run.',
        });
      } else {
        setToast({
          kind: 'info',
          title: 'Reconciliation started',
          message: refreshGam
            ? 'Refreshing the tracker + GAM delivery first — this can take several minutes.'
            : 'Scanning IOs and invoices (cached data)…',
        });
      }
      load();
    } catch (e: any) {
      startedAtRef.current = 0;
      setInFlight(false);
      setError(e?.message || 'Failed to start');
      setToast({ kind: 'error', title: 'Could not start run', message: e?.message });
    } finally {
      setStarting(false);
    }
  };

  const canManage = !!status?.canManage;
  const report = lastReport;

  const visibleFindings = useMemo(() => {
    const all = report?.findings ?? [];
    const filtered = showOnlyFlagged ? all.filter((f) => f.worstSeverity !== 'ok') : all;
    const sorted = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'advertiser':
          return a.advertiser.localeCompare(b.advertiser);
        case 'flightEnd':
          return (b.flightEnd ?? '').localeCompare(a.flightEnd ?? '');
        case 'severity':
          return SEV_RANK[b.worstSeverity] - SEV_RANK[a.worstSeverity] || b.totalDollarsAtRisk - a.totalDollarsAtRisk;
        case 'risk':
        default:
          return b.totalDollarsAtRisk - a.totalDollarsAtRisk || SEV_RANK[b.worstSeverity] - SEV_RANK[a.worstSeverity];
      }
    });
    return sorted;
  }, [report?.findings, showOnlyFlagged, sortKey]);

  const cleanCount = (report?.findings ?? []).filter((f) => f.worstSeverity === 'ok').length;

  const controlRow = (
    <div style={styles.controlRow}>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>Flight ended from (days ago)</span>
        <input type="number" min={1} max={400} value={maxDays} onChange={(e) => setMaxDays(Number(e.target.value))} style={styles.input} />
      </label>
      <label style={styles.field}>
        <span style={styles.fieldLabel}>to (days ago)</span>
        <input type="number" min={0} max={399} value={minDays} onChange={(e) => setMinDays(Number(e.target.value))} style={styles.input} />
      </label>
      <label style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={refreshGam} onChange={(e) => setRefreshGam(e.target.checked)} />
        <span style={styles.fieldLabel}>Refresh source data — tracker + GAM (recommended; slower)</span>
      </label>
      <button
        type="button"
        onClick={onRun}
        disabled={!canManage || starting || inFlight}
        title={!canManage ? 'Requires the invoicing.manage permission' : undefined}
        style={{ ...styles.runBtn, ...((!canManage || starting || inFlight) ? styles.runBtnDisabled : {}) }}
      >
        {inFlight ? 'Running…' : starting ? 'Starting…' : 'Run reconciliation'}
      </button>

      {report && (
        <div style={styles.viewControls}>
          <label style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showOnlyFlagged} onChange={(e) => setShowOnlyFlagged(e.target.checked)} />
            <span style={styles.fieldLabel}>Only flagged{cleanCount ? ` (hides ${cleanCount} clean ✓)` : ''}</span>
          </label>
          <label style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={hidePending} onChange={(e) => setHidePending(e.target.checked)} />
            <span style={styles.fieldLabel}>Hide not-yet-due months</span>
          </label>
          <label style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <span style={styles.fieldLabel}>Sort</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} style={styles.select}>
              <option value="risk">$ at risk</option>
              <option value="severity">Severity</option>
              <option value="advertiser">Advertiser</option>
              <option value="flightEnd">Flight end</option>
            </select>
          </label>
        </div>
      )}
    </div>
  );

  const topBarRight = report ? (
    <div style={styles.metaRight}>
      <div>Last run {fmtWhen(report.generatedAt)}</div>
      <div style={{ color: 'var(--fg-4)' }}>
        Flight end {report.window.startDate} → {report.window.endDate}
      </div>
    </div>
  ) : undefined;

  return (
    <DashboardFrame eyebrow="Finance" title="IO ⇄ Invoice Reconciliation" topBarRight={topBarRight} controlRow={controlRow}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 50 }}>
          <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}

      {error && (
        <div style={styles.errorBanner}>{error}</div>
      )}

      {inFlight && (
        <div style={styles.runningBanner}>
          <LoadingDots />
          <span>
            Reconciliation running{state?.startedAt ? ` (started ${fmtWhen(state.startedAt)})` : ''}. The latest completed report
            stays below until this finishes.
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><LoadingDots /></div>
      ) : !report ? (
        <EmptyState
          title="No reconciliation yet"
          subtitle="Run a reconciliation to review campaigns whose flight ended in the selected window (default: the last 120 days) against their invoices, month by month, and surface any under-billing or missed revenue."
          cta={
            <button type="button" onClick={onRun} disabled={!canManage || starting || inFlight} style={{ ...styles.runBtn, ...((!canManage || starting || inFlight) ? styles.runBtnDisabled : {}) }}>
              {inFlight ? 'Running…' : 'Run reconciliation'}
            </button>
          }
        />
      ) : (
        <>
          <div style={styles.kpiRow}>
            <KpiCard label="$ at risk (due only)" value={fmtUsd(report.totals.totalDollarsAtRisk)} featured />
            <KpiCard label="Months planned, not billed" value={report.totals.monthsPlannedNotBilled ?? report.totals.byCheck?.plannedNotBilled ?? 0} />
            <KpiCard label="Months billed, not invoiced" value={report.totals.monthsBilledNotInvoiced ?? report.totals.byCheck?.billedNotInvoiced ?? 0} />
            <KpiCard label="Under-billed campaigns" value={report.totals.underBilledCompleteCampaigns ?? report.totals.byCheck?.underBilling ?? 0} />
            <KpiCard label="Invoices, no campaign" value={report.totals.byCheck?.untrackedInvoice ?? 0} />
            <KpiCard label="Campaigns in window" value={report.totals.campaignsInWindow} />
          </div>

          <div style={styles.subStats}>
            {report.totals.bySeverity.critical} critical · {report.totals.bySeverity.warning} warning · {report.totals.findings} flagged ·{' '}
            {report.totals.iosScanned} IO files scanned
            {typeof report.totals.pendingDollarsDeferred === 'number' && report.totals.pendingDollarsDeferred > 0
              ? ` · ${fmtUsd(report.totals.pendingDollarsDeferred)} planned but not yet due`
              : ''}
          </div>

          <div style={styles.legend}>
            <strong>Reconciled per billing month.</strong> Invoices are issued monthly, not at flight end — each campaign rolls up its
            months (expand a row to see them). Months that aren't due yet are shown muted and <em>excluded from $ at risk</em>, so a
            mid-flight or just-ended campaign never looks under-billed. Impressions (delivered / contracted) and the tracker's Paid
            status are shown for context only — neither drives a check. Only campaigns whose <em>flight ended</em> in the window appear.
          </div>

          {report.scannedInvoicePdfs === false && (
            <div style={styles.cachedBanner}>
              ⚠️ Cached run — invoice PDFs and GAM delivery were <strong>not</strong> scanned, so the Invoiced columns and the
              invoice cross-checks are unavailable. Re-run with "Refresh source data" checked to verify invoicing.
            </div>
          )}

          {report.sheetUrl && (
            <div>
              <a href={report.sheetUrl} target="_blank" rel="noreferrer" style={styles.sheetLink}>
                📄 Open the reconciliation Google Sheet
              </a>
            </div>
          )}

          {report.notes.length > 0 && (
            <details style={styles.notes}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Notes & data gaps ({report.notes.length})</summary>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {report.notes.map((n, i) => (
                  <li key={i} style={{ fontSize: 12.5, color: 'var(--fg-3)', marginBottom: 4 }}>{n}</li>
                ))}
              </ul>
            </details>
          )}

          {report.findings.length === 0 ? (
            <EmptyState brand={false} icon="✅" title="No campaigns in window" subtitle="No campaigns had a flight ending in this window." />
          ) : visibleFindings.length === 0 ? (
            <EmptyState
              brand={false}
              icon="✅"
              title="No discrepancies found"
              subtitle={`Every in-window campaign reconciles. Toggle "Only flagged" off to review all ${report.findings.length} campaigns.`}
            />
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {COLUMNS.map((h, i) => (
                      <th key={i} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleFindings.map((f: ReconciliationFinding) => (
                    <MonthLedgerRow
                      key={f.id}
                      finding={f}
                      expanded={expandedId === f.id}
                      onToggle={() => setExpandedId((cur) => (cur === f.id ? null : f.id))}
                      colCount={COL_COUNT}
                      hidePending={hidePending}
                      scannedInvoicePdfs={report.scannedInvoicePdfs}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </DashboardFrame>
  );
};

const styles: Record<string, React.CSSProperties> = {
  controlRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 16,
    padding: '12px 32px',
    flexWrap: 'wrap',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: 'var(--fg-3)' },
  input: { width: 90, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-1)', fontSize: 13 },
  runBtn: {
    padding: '9px 16px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontWeight: 600,
    fontSize: 13.5,
    cursor: 'pointer',
  },
  runBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  metaRight: { fontSize: 12, color: 'var(--fg-3)', textAlign: 'right', lineHeight: 1.5 },
  errorBanner: { padding: '10px 14px', borderRadius: 8, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 13 },
  runningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderRadius: 8,
    background: 'var(--info-soft)',
    color: 'var(--fg-2)',
    fontSize: 13,
  },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 },
  subStats: { fontSize: 12, color: 'var(--fg-3)' },
  legend: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--fg-2)',
    background: 'var(--info-soft)',
    border: '1px solid var(--border-1)',
    borderRadius: 8,
    padding: '10px 14px',
  },
  cachedBanner: {
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--fg-2)',
    background: 'var(--warning-soft)',
    border: '1px solid var(--border-1)',
    borderRadius: 8,
    padding: '10px 14px',
  },
  viewControls: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginLeft: 'auto' },
  select: { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-1)', fontSize: 13, background: 'var(--bg-surface-1)' },
  sheetLink: { fontSize: 13.5, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' },
  notes: { background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)', borderRadius: 8, padding: '10px 14px' },
  tableWrap: { ...tableStyles.wrap, overflow: 'auto' },
  table: { ...tableStyles.table, fontSize: 13 },
  th: tableStyles.thSticky,
  td: { padding: 12, verticalAlign: 'top', color: 'var(--fg-1)' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
};

export default ReconciliationDashboard;
